import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { LockIcon } from 'lucide-react';
import { ScaledViewport } from './ScaledViewport';
import { FerringWordmark } from './FerringWordmark';
import { TeaserSnak } from './teaser/TeaserSnak';
type Mode = 'phone' | 'desktop';
type LivePrototypeProps = {
  /** Increment to restart the Snak and draw attention to it. */
  playKey: number;
};

/** The playable REKOVELLE teaser Snak, shown in a phone or desktop frame. */
export function LivePrototype({
  playKey
}: LivePrototypeProps) {
  const [mode, setMode] = useState<Mode>('phone');
  const [pulse, setPulse] = useState(false);
  const deviceRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (playKey === 0) return;
    setPulse(true);
    const focusTimer = setTimeout(() => {
      deviceRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({
        preventScroll: true
      });
    }, 250);
    const pulseTimer = setTimeout(() => setPulse(false), 1400);
    return () => {
      clearTimeout(focusTimer);
      clearTimeout(pulseTimer);
    };
  }, [playKey]);
  return <div className="flex h-full w-full flex-col items-center" data-id="element-134" data-mp="u5zzrdd4d9f2">
      <div className="flex w-full flex-shrink-0 items-center justify-between gap-4" data-id="element-135" data-mp="gdtljwtj1ci9">
        <p className="flex items-center gap-2 text-[clamp(0.6rem,0.8vw,0.74rem)] font-bold uppercase tracking-[0.22em] text-[var(--fg)]" data-id="element-136" data-mp="gbqlsl2ko50c">
          <span className="relative flex h-2 w-2" data-id="element-137" data-mp="184hlj5jgsbuc">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--brand)] opacity-50" data-id="element-138" data-mp="182wq7dpbsovf" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--brand)]" data-id="element-139" data-mp="z4badqb8nnyc" />
          </span>
          Live prototype
        </p>
        <div role="radiogroup" aria-label="Preview device" className="flex rounded-full border border-[var(--fg)]/10 bg-white p-0.5 hide-on-print" data-id="element-140" data-mp="141tdv16hlayy">
          {(['phone', 'desktop'] as const).map(m => <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => setMode(m)} className={`relative rounded-full px-3 py-1 text-[clamp(0.58rem,0.75vw,0.7rem)] font-bold uppercase tracking-[0.16em] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] ${mode === m ? 'text-white' : 'text-[var(--muted)] hover:text-[var(--fg)]'}`} data-id="element-141" data-mp="3ouy5299hbrpd">
              {mode === m && <motion.span layoutId="device-toggle" className="absolute inset-0 rounded-full bg-[var(--fg)]" transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-142" data-mp="16n11u2u3gy9d" />}
              <span className="relative" data-id="element-143" data-mp="xn5n5o1byl4l">{m}</span>
            </button>)}
        </div>
      </div>

      <div ref={deviceRef} data-snak-device className="relative mt-[clamp(0.6rem,1.2vw,1rem)] min-h-0 w-full flex-1" data-id="element-144" data-mp="2w30i03w4rnyi">
        {mode === 'phone' ? <ScaledViewport baseWidth={390} baseHeight={760} bezel={10} radius={0.13} className="h-full w-full" deviceClassName={`bg-ink shadow-[0_40px_80px_-30px_rgba(14,27,51,0.5)] transition-shadow duration-500 ${pulse ? 'ring-4 ring-[var(--brand)]/40' : ''}`} data-id="element-145" data-mp="2s1omjhqks8ie">
            <div className="flex h-full flex-col bg-white" data-id="element-146" data-mp="rl2c9gurwxwi">
              <TeaserSnak key={playKey} data-id="element-147" data-mp="rmn4v0xj26rq" />
            </div>
          </ScaledViewport> : <ScaledViewport baseWidth={1240} baseHeight={820} bezel={8} radius={0.018} className="h-full w-full" deviceClassName={`bg-ink shadow-[0_40px_80px_-30px_rgba(14,27,51,0.5)] ${pulse ? 'ring-4 ring-[var(--brand)]/40' : ''}`} data-id="element-148" data-mp="3r1qwyx4xgu53">
            <div className="flex h-full flex-col bg-paper" data-id="element-149" data-mp="1fa54fin1a959">
              <div className="flex h-10 flex-shrink-0 items-center gap-3 border-b border-rule bg-white px-4" data-id="element-150" data-mp="1f9lizry6d2mh">
                <span className="flex gap-1.5" aria-hidden="true" data-id="element-151" data-mp="1k7ddmtq7f2ud">
                  <span className="h-3 w-3 rounded-full bg-ink/15" data-id="element-152" data-mp="1it3uyymnfmy1" />
                  <span className="h-3 w-3 rounded-full bg-ink/15" data-id="element-153" data-mp="1dt7rr1lrzzjj" />
                  <span className="h-3 w-3 rounded-full bg-ink/15" data-id="element-154" data-mp="1dv2fsoscg7ru" />
                </span>
                <span className="mx-auto flex items-center gap-1.5 rounded-md bg-paper px-4 py-1 font-mono text-[11px] text-graphite" data-id="element-155" data-mp="1ru1yzgx37ugm">
                  <LockIcon size={10} aria-hidden="true" data-id="element-156" data-mp="21qn9uivsfzsd" />
                  hcp.ferring.com/rekovelle/snak
                </span>
              </div>
              <div className="flex min-h-0 flex-1" data-id="element-157" data-mp="1wral1slcwin5">
                <aside className="flex w-[360px] flex-col justify-between border-r border-rule px-10 py-10" data-id="element-158" data-mp="2bgggd5h2popr">
                  <div data-id="element-159" data-mp="26hdchoctxojd">
                    <FerringWordmark data-id="element-160" data-mp="26iy6ncv8f5gp" />
                    <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-graphite" data-id="element-161" data-mp="26jzllsfdcc3p">For healthcare professionals</p>
                    <p className="mt-3 text-[26px] font-semibold leading-tight tracking-[-0.03em] text-ink" data-id="element-162" data-mp="2fj33it2sulgm">REKOVELLE interactive</p>
                    <p className="mt-3 text-[14px] leading-snug text-graphite" data-id="element-163" data-mp="2z4mt8hxclph5">A 40-second case. Two patients. One clinical question.</p>
                  </div>
                  <p className="text-[10px] leading-snug text-graphite/70" data-id="element-164" data-mp="2q3yhxuzca37y">Conceptual prototype for discussion.</p>
                </aside>
                <div className="flex flex-1 items-center justify-center py-3" data-id="element-165" data-mp="2u3flujz4asb0">
                  <div className="flex h-full w-[400px] flex-col overflow-hidden rounded-xl bg-white shadow-[0_20px_40px_-20px_rgba(14,27,51,0.25)] ring-1 ring-rule" data-id="element-166" data-mp="2gcki1m3h71ea">
                    <TeaserSnak key={playKey} data-id="element-167" data-mp="26fpo7gi5dp6c" />
                  </div>
                </div>
                <div className="w-[200px]" aria-hidden="true" data-id="element-168" data-mp="2be9bbk8h970a" />
              </div>
            </div>
          </ScaledViewport>}
      </div>
    </div>;
}